import { IEstimateFormSnapshot } from "pages/CreateOrder/utils/estimateFormSnapshot";

/**
 * CLE-3043 — where the one global staff chat widget appears, which account it chats as, and what it
 * tells the API about the page it is on. The rules read only the URL, so they are tested here rather
 * than through the router.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const segmentsOf = (pathname: string): string[] =>
  pathname.split("/").filter(Boolean);

/**
 * "A hospital page" = a page a hospital staff member can reach for their current account
 * (routes.tsx: the routes under AppLayout that are not SuperAdmin-only):
 *
 * - `/hospitals/view/:id` and every tab under it (orders, providers, analytics, patients, reports,
 *   doctor revenue, patient chats, contracts and each contract page);
 * - `/orders` and everything under it (the Estimates list, an estimate, create / copy / review);
 * - `/providers/new`, `/providers/edit/:providerId`;
 * - `/account` (My account).
 *
 * Not a hospital page: every SuperAdmin-only global page (`/chatbot`, `/users`, `/fee-schedule`,
 * `/payer-rules` — including its un-scoped `contracts/:id`, whose contract can belong to another
 * account than the current one — `/customize`, `/reports`, `/revenue-dashboard`, `/eligibility`,
 * `/integrations`, `/contract-model-drafts`, `/patients`, `/hst-sync`, and the `/hospitals` list /
 * new / edit admin screens), and `/training` (the Clear Certified quiz: a bot that answers questions
 * has no place next to a certification quiz). The patient-facing public pages are rendered by
 * PublicLayout, never AppLayout, so the global mount is never on them.
 */
export const isHospitalPage = (pathname: string): boolean => {
  const [first, second, third] = segmentsOf(pathname);
  if (first === "hospitals") return second === "view" && !!third;
  if (first === "orders") return true;
  if (first === "providers") return !!second;
  if (first === "account") return !second;
  return false;
};

/**
 * Where the account the widget chats as comes from — what the page is ABOUT, never just Redux's
 * `currentAccount`, which after a full reload is the user's default account (UserAccountProvider:
 * `userInfo.defaultAccount || accountIds[0]`), for a SuperAdmin usually some other hospital. In order:
 *
 * 1. `url` — `/hospitals/view/:id/...` with a UUID `:id`: the URL's `:id`. The contract page
 *    (`.../contracts/:contractId`) is not rendered by ViewHospitals, so nothing has synced
 *    `currentAccount` to the URL when a SuperAdmin opens it from a link, and a connected contract
 *    viewed from a hospital must chat as THAT hospital (the CLE-2811 rule the contract page's own mount
 *    had). A non-UUID `:id` names no account: `current`.
 * 2. `order` — `/orders/:id` (OrderViewPage), `/orders/copy/:id` (CopyOrder) and
 *    `/orders/review/:id` (ReviewOrder — a draft is an Order, loaded by the same `GET /orders/:id`):
 *    the estimate's own account, read by useStaffChatAccountId from the order the page loads. Always,
 *    whatever `?accountId=` says: the page is about that estimate, and a query cannot move it.
 * 3. `query` — `?accountId=<uuid>` anywhere else (the Estimates list's create links carry it).
 *    useStaffChatAccountId uses it only when the user may use that account (canUseQueryAccount).
 * 4. `current` — everything else (`/orders`, `/orders/new` without a query, `/account`,
 *    `/providers/...`): `currentAccount`.
 *
 * The widget sends this account on its own requests and never changes the app's `currentAccount`.
 */
export type StaffChatAccountSource =
  | { kind: "url"; accountId: string }
  | { kind: "order"; orderId: string }
  | { kind: "query"; accountId: string }
  | { kind: "current" };

const orderIdOf = (segments: string[]): string | undefined => {
  const [first, second, third, fourth] = segments;
  if (first !== "orders") return undefined;
  if (second && !third && UUID.test(second)) return second;
  if (
    (second === "copy" || second === "review") &&
    third &&
    !fourth &&
    UUID.test(third)
  ) {
    return third;
  }
  return undefined;
};

export const resolveStaffChatAccountSource = (
  pathname: string,
  search: string
): StaffChatAccountSource => {
  const segments = segmentsOf(pathname);
  const [first, second, third] = segments;
  if (first === "hospitals" && second === "view" && third) {
    return UUID.test(third)
      ? { kind: "url", accountId: third }
      : { kind: "current" };
  }

  const orderId = orderIdOf(segments);
  if (orderId) return { kind: "order", orderId };

  const queryAccountId = new URLSearchParams(search).get("accountId");
  if (queryAccountId && UUID.test(queryAccountId)) {
    return { kind: "query", accountId: queryAccountId };
  }

  return { kind: "current" };
};

/**
 * May the widget chat as the `?accountId=` account? A SuperAdmin may use any account; anyone else only
 * one of their own (`userInfo.accountIds`). The API refuses a foreign account anyway
 * (checkStaffContractChatAccess); this keeps the widget from offering a chat it would refuse.
 */
export const canUseQueryAccount = (
  accountId: string,
  isSuperAdmin: boolean,
  accountIds: string[] | undefined
): boolean => isSuperAdmin || !!accountIds?.includes(accountId);

export type StaffChatSurface =
  | "estimate"
  | "contract"
  | "contracts-library"
  | "smart-insights"
  | "general";

export interface IStaffChatPageData {
  /** Why "Create and Send" is disabled, as labels (never field values). Estimate pages only. */
  blockingIssues?: string[];
  /**
   * CLE-3043 follow-up — what has been typed into the estimate form so far (buildEstimateFormSnapshot).
   * On the review / copy pages the API reads it beside the saved estimate (it carries the unsaved edits).
   */
  form?: IEstimateFormSnapshot;
}

export interface IStaffChatContextData {
  pageUrl: string;
  orderId?: string;
  /**
   * Where `orderId` came from. `"expandedRow"`: the row expanded on an Estimates list, which the
   * bot names in its answer because the list shows other estimates too. `"reviewPage"`: the review /
   * edit page of a saved estimate (`/orders/review/:id`, a Draft usually). `"copyPage"`: the create
   * page copying an estimate (`/orders/copy/:id`) — a NEW estimate for the same patient. The
   * estimate page sends nothing (the API reads that as `"page"`).
   */
  orderIdSource?: "page" | "expandedRow" | "reviewPage" | "copyPage";
  contractFeeScheduleId?: string;
  page?: IStaffChatPageData;
}

export interface IStaffChatSurface {
  surface: StaffChatSurface;
  /**
   * Sent with EVERY message. The API reads the contract scope per message (resolveContractScope): a
   * message carrying context without `contractFeeScheduleId` is a page with no single contract, which
   * is what lets one chat move between the Library tab and a contract page.
   */
  contextData: IStaffChatContextData;
}

/**
 * The page → chat context mapping. No suggested questions: the staff widget shows none
 * (2026-10-29, see resolveSuggestedQuestions in components/ChatWidget/chatWidget.helpers.ts).
 *
 * - `/orders/:id` → `orderId` (also when its Redo form is open: Redo is not a route of its own).
 * - `/orders/review/:id` → `orderId` + `orderIdSource: "reviewPage"`, and `/orders/copy/:id` →
 *   `orderId` + `orderIdSource: "copyPage"` (CLE-3043 follow-up — Ani asked "when is this patient's
 *   DOS" on a Draft's review page and got "I can't see which estimate you have open"). Both are the
 *   `estimate` surface; the id is read by the API inside the session's account, as on `/orders/:id`.
 * - `/hospitals/view/:id/contracts/:contractId` (and its `/preview`, `/edit`) → `contractFeeScheduleId`
 *   — CLE-2811's contract-page scoping. The id must be a UUID, so a sub-tab name (`library`,
 *   `claims`, `new`, …) can never be sent as a contract id.
 * - Contracts → Library, and Contracts → Smart Insights (also the tab's default) → their own
 *   surface, page URL only.
 * - Anything else → `general`, page URL only.
 */
export const resolveStaffChatSurface = (
  pathname: string,
  href: string
): IStaffChatSurface => {
  const segments = segmentsOf(pathname);
  const pageUrl = href;

  if (
    segments[0] === "orders" &&
    segments.length === 2 &&
    UUID.test(segments[1])
  ) {
    return {
      surface: "estimate",
      contextData: { pageUrl, orderId: segments[1] },
    };
  }

  if (
    segments[0] === "orders" &&
    (segments[1] === "review" || segments[1] === "copy") &&
    segments.length === 3 &&
    UUID.test(segments[2])
  ) {
    return {
      surface: "estimate",
      contextData: {
        pageUrl,
        orderId: segments[2],
        orderIdSource: segments[1] === "review" ? "reviewPage" : "copyPage",
      },
    };
  }

  if (
    segments[0] === "hospitals" &&
    segments[1] === "view" &&
    segments[3] === "contracts"
  ) {
    const [, , , , contractSegment, action] = segments;

    if (
      contractSegment &&
      UUID.test(contractSegment) &&
      (segments.length === 5 ||
        (segments.length === 6 && (action === "preview" || action === "edit")))
    ) {
      return {
        surface: "contract",
        contextData: { pageUrl, contractFeeScheduleId: contractSegment },
      };
    }

    if (segments.length === 5 && contractSegment === "library") {
      return {
        surface: "contracts-library",
        contextData: { pageUrl },
      };
    }

    if (
      segments.length === 4 ||
      (segments.length === 5 && contractSegment === "smart-insights")
    ) {
      return { surface: "smart-insights", contextData: { pageUrl } };
    }
  }

  return {
    surface: "general",
    contextData: { pageUrl },
  };
};

/**
 * The shared contract with the API: at most this many issues, each at most this long. They mirror
 * MAX_BLOCKING_ISSUES / MAX_BLOCKING_ISSUE_LENGTH in services/clear-api/src/domains/chats/chat.dto.ts
 * (the SPA cannot import from the API); staffChatWidget.helpers.test.ts pins both at 20 and 80.
 */
export const MAX_BLOCKING_ISSUES = 20;
export const MAX_BLOCKING_ISSUE_LENGTH = 80;

/**
 * Every character the API's BLOCKING_ISSUE_LABEL_PATTERN (chat.dto.ts) allows in a label. The API
 * splices the labels into the bot's system prompt and DROPS any label with another character, so
 * the widget strips them first: a real label always reaches the bot.
 */
const DISALLOWED_LABEL_CHARACTERS = /[^A-Za-z0-9 ,.'\u2019()/&%$:-]+/g;

/** A label as the API accepts it: disallowed characters become a space, starts with a letter or digit. */
export const toAllowedLabel = (label: string): string =>
  label
    .replace(DISALLOWED_LABEL_CHARACTERS, " ")
    .replace(/ {2,}/g, " ")
    .replace(/^[^A-Za-z0-9]+/, "")
    .trim();

/**
 * CLE-3043 — the page's blocking issues as the API accepts them: stripped to the API's character
 * allow-list, trimmed, blanks and repeats dropped, each cut to MAX_BLOCKING_ISSUE_LENGTH (ending in
 * "..." when cut — the API refuses "…"), the first MAX_BLOCKING_ISSUES kept.
 */
export const capBlockingIssues = (issues: readonly unknown[]): string[] => {
  const out: string[] = [];
  issues.forEach((issue) => {
    if (out.length >= MAX_BLOCKING_ISSUES || typeof issue !== "string") return;
    const allowed = toAllowedLabel(issue);
    if (!allowed) return;
    const capped =
      allowed.length > MAX_BLOCKING_ISSUE_LENGTH
        ? `${allowed.slice(0, MAX_BLOCKING_ISSUE_LENGTH - 3).trimEnd()}...`
        : allowed;
    if (!out.includes(capped)) out.push(capped);
  });
  return out;
};

/**
 * Adds `page: { blockingIssues, form }` when the page published either (`null` = the page publishes
 * nothing, so no `page` key at all). An empty list is sent: "nothing is blocking" is an answer.
 * `form` (CLE-3043 follow-up) is the typed estimate form, already cut to the API's limits by
 * buildEstimateFormSnapshot; `null` = nothing typed, so no `form` key.
 */
export const withPageHints = (
  contextData: IStaffChatContextData,
  blockingIssues: readonly unknown[] | null,
  form: IEstimateFormSnapshot | null = null
): IStaffChatContextData =>
  blockingIssues || form
    ? {
        ...contextData,
        page: {
          ...(blockingIssues
            ? { blockingIssues: capBlockingIssues(blockingIssues) }
            : {}),
          ...(form ? { form } : {}),
        },
      }
    : contextData;

/**
 * CLE-3043 — the estimate in focus on an Estimates list (StaffChatPageHintsContext
 * `focusedOrderId`, published by OrdersTable) as the page's estimate. Only on a surface with no
 * estimate of its own: `/orders/:id` keeps its URL's order whatever the context holds. The id
 * must be a UUID — it is an order id, never a label — and nothing is added for `null`.
 *
 * It is sent with `orderIdSource: "expandedRow"`. The API reads it like the estimate page's own
 * id (`<estimate_on_page>` and the estimate tool's default, both inside the session's account),
 * but tells the bot it is an expanded row: start the answer by naming the estimate, since the
 * user may mean another one on the same list.
 */
export const withFocusedOrder = (
  contextData: IStaffChatContextData,
  focusedOrderId: string | null
): IStaffChatContextData =>
  !contextData.orderId && focusedOrderId && UUID.test(focusedOrderId)
    ? { ...contextData, orderId: focusedOrderId, orderIdSource: "expandedRow" }
    : contextData;
